import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  formatPO,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { cloneDeep, every, get, isEmpty, isNumber, sortBy } from "lodash";
import { diff } from "deep-object-diff";

export const ORDER_FIELDS = {
  vendor: "",
  orderPO: "",
  date: null,
  totalCost: null,
  document: null,
  missingDocument: false,
  reason: "",
  description: "",
  issues: [],
  saleLabors: [],
  laborer: null,
  manHours: null,
  notes: "",
  directShip: false,
  noMaterial: false,
  locked: false,

  // An additional discount off standard cost negotiated for this order.
  // When expected, the acknowledged and billed costs are checked against the
  // discounted target and flagged for review if the discount is not reflected.
  additionalDiscountExpected: false,
  additionalDiscountPercent: null,

  // Status
  fullyAcked: false,
  fullyBilled: false,
  fullyPaid: false,
  fullyReceived: false,
  qcInspected: false,
  fullyResolvedCritical: true,
  fullyResolved: true,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(ORDER_FIELDS);

export const ORDER_STATUS_TYPES = {
  ack: {
    order: 1,
    label: "Awaiting acknowledgement",
    icon: "ack",
    color: "warning",
  },
  production: {
    order: 2,
    label: "In production",
    icon: "vendor",
    color: "positive",
  },
  scheduling: {
    order: 2,
    label: "Scheduling",
    icon: "scheduling",
    color: "warning",
  },
  qc: {
    order: 3,
    label: "Quality Control",
    icon: "quality_control",
    color: "warning",
  },
  issuesCritical: {
    order: 4,
    label: "Critical sale issues",
    icon: "issue",
    color: "negative",
  },
  billing: {
    order: 5,
    label: "Awaiting bill",
    icon: "bill",
    color: "warning",
  },
  issues: {
    order: 6,
    label: "Unresolved sale issues",
    icon: "issue",
    color: "warning",
  },
  paying: {
    order: 7,
    label: "Ready to pay",
    icon: "paying",
    color: "warning",
  },
  complete: {
    order: 8,
    label: "Complete",
    icon: "done",
    color: "positive",
  },
};
deepFreeze(ORDER_STATUS_TYPES);

// Discount-review tolerance: the acknowledged/billed cost may come in slightly
// above the discounted target (rounding, minor fees) without raising a flag.
// The tolerance is the greater of 1% of the target or $5.
export const DISCOUNT_TOLERANCE_PERCENT = 0.01;
export const DISCOUNT_TOLERANCE_MIN = 5;

// True when `actual` exceeds `target` by more than the allowed tolerance —
// i.e. the expected discount is not clearly reflected in the actual cost.
export const discountShortfall = (actual, target) => {
  if (!isNumber(actual) || !isNumber(target)) {
    return false;
  }
  const tolerance = Math.max(
    Math.abs(target) * DISCOUNT_TOLERANCE_PERCENT,
    DISCOUNT_TOLERANCE_MIN
  );
  return actual - target > tolerance;
};

// One vendor's orders on a sale, reviewed at billing against the additional
// discount they expect: what was billed for them should land at or below
// the quote's cost for the vendor (`quoteExpected`, the "standard cost")
// less the discount dollars each discount order expects. `{ target, billed }`
// when it doesn't, else null. Shared by a sale's Orders and the project's
// Vendor tab.
export const vendorDiscountReview = (orders, quoteExpected, bills) => {
  const discountOrders = orders.filter((o) => o.expectsAdditionalDiscount);
  if (!discountOrders.length || !quoteExpected) return null;
  const expectedDiscount = discountOrders.reduce(
    (sum, o) =>
      sum + (o.data.totalCost || 0) * (o.data.additionalDiscountPercent / 100),
    0
  );
  const target = quoteExpected - expectedDiscount;
  const orderIDs = new Set(orders.map((o) => o.id));
  const billed = bills
    .filter((bill) => orderIDs.has(bill.orderID) && !bill.isCreditMemo)
    .reduce((sum, bill) => sum + (bill.data.totalCost || 0), 0);
  return billed > 0 && discountShortfall(billed, target)
    ? { target, billed }
    : null;
};

export default class Order extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    // Backward compatibility
    if (!data.orderPO) {
      data.orderPO = projectID + saleID;
    }

    data = sanitize(data, ORDER_FIELDS);

    // Convert to Date
    data.date = timestampToDate(data.date);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    if (this.data.document) {
      this.data.document = this.document.data;
    }
  }

  get isLabor() {
    return this.data.vendor === "Labor";
  }

  // An additional discount off standard cost is expected for this order.
  get expectsAdditionalDiscount() {
    return Boolean(
      this.data.additionalDiscountExpected &&
        isNumber(this.data.additionalDiscountPercent) &&
        this.data.additionalDiscountPercent > 0
    );
  }

  // Multiplier applied to the standard cost once the expected additional
  // discount is taken (e.g. 10% expected -> 0.9).
  get discountFactor() {
    return this.expectsAdditionalDiscount
      ? 1 - this.data.additionalDiscountPercent / 100
      : 1;
  }

  // The acknowledged/billed cost should land at or below this once the
  // expected additional discount is applied to the order's standard cost.
  // Returns null when no additional discount is expected or the order has no
  // total cost to discount from.
  get discountedCostTarget() {
    return this.expectsAdditionalDiscount && isNumber(this.data.totalCost)
      ? roundPrice(this.data.totalCost * this.discountFactor)
      : null;
  }

  // Permissions

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee &&
      (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      !this.locked &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Order.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/orders`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(order) {
    return order.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Order(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      orderPO: [(a) => Boolean(a) || "required"],
      date: [(a) => Boolean(a) || "required"],
      vendor: [(a) => Boolean(a) || "required"],
      totalCost: [(a) => (isNumber(a) && a >= 0) || "totalCostInvalid"],
      document: [
        () =>
          Boolean(
            this.data.missingDocument ||
              (this.data.document && this.document.isValid)
          ) || "validDocumentRequired",
      ],
      reason: [
        (a) =>
          Boolean(!this.data.missingDocument || a.trim().length) ||
          "reasonRequiredMissingDocument",
      ],
      laborer: [(a) => !this.isLabor || Boolean(a) || "required"],
      description: [
        (a) =>
          (!this.isLabor && this.data.document && this.document.isValid) ||
          Boolean(a) ||
          "required",
      ],
      manHours: [
        (a) => !this.isLabor || (isNumber(a) && a > 0) || "manHoursPositive",
      ],
      additionalDiscountPercent: [
        (a) =>
          !this.data.additionalDiscountExpected ||
          (isNumber(a) && a > 0 && a <= 100) ||
          "discountPercentInvalid",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isFinalOrder() {
    const sale = this.sale;
    if (!this.id || !sale || !this.vendor) {
      return null;
    }

    const vendors = sale.data?.status?.vendors || {};
    const exactVendorStatus = vendors[this.vendor];
    if (
      exactVendorStatus &&
      typeof exactVendorStatus === "object" &&
      Object.prototype.hasOwnProperty.call(exactVendorStatus, "fullyOrdered")
    ) {
      return exactVendorStatus.fullyOrdered;
    }

    const segments = this.vendor.split(".");
    let cursor = vendors[segments[0]];
    for (let i = 1; i < segments.length; i += 1) {
      if (!cursor || typeof cursor !== "object") {
        return null;
      }
      cursor = cursor[segments[i]];
    }

    return cursor &&
      typeof cursor === "object" &&
      Object.prototype.hasOwnProperty.call(cursor, "fullyOrdered")
      ? cursor.fullyOrdered
      : null;
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(diff({ ...this.data, date: null, vendor: "" }, ORDER_FIELDS))
    );
  }

  isEqual(order) {
    return (
      this.id === order.id &&
      isEmpty(
        diff(
          Order.sanitize(cloneDeep(this.data)),
          Order.sanitize(cloneDeep(order.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.document) {
      const doc = new DocumentRef(data.document);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.document = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    if (!data.additionalDiscountExpected) {
      data.additionalDiscountPercent = null;
    }
    if (data.vendor === "Labor") {
      data.fullyAcked = true;
      data.fullyReceived = true;
      data.qcInspected = true;
    } else {
      if (data.missingDocument) {
        data.document = null;
      } else {
        data.reason = "";
        data.description = "";
      }
      Order.cleanDocuments(data, removeUnsaved);
    }
    return data;
  }

  // Formatting

  get project() {
    return this.projectID
      ? store.state.main.projects[this.projectID] || null
      : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID] || null
      : null;
  }

  get po() {
    return formatPO(this.projectID + this.saleID, this.data.orderPO);
  }

  /**
   * Everything the order is owed is off the truck on a slip that still waits
   * for QC. The order only counts as received once QC makes its items ready,
   * so this is what tells "Quality Control" from "In production" until then
   * -- the same reading of the sale's slip entries as computeSaleStatus.
   */
  get isAwaitingQC() {
    const slips = this.sale?.data?.status?.packingSlips || {};
    return Object.values(slips).some(
      (slip) =>
        slip &&
        slip.orderID === this.id &&
        slip.receivedComplete === true &&
        !slip.qcInspected
    );
  }

  get acks() {
    return sortBy(
      Object.values(store.state.main.acks).filter(
        (ack) => ack.orderID === this.id
      ),
      ["expectedDate", "created"]
    );
  }

  get bills() {
    return sortBy(
      Object.values(store.state.main.bills).filter(
        (bill) => bill.orderID === this.id
      ),
      ["invoiceDate", "created"]
    );
  }

  get packingSlips() {
    return sortBy(
      Object.values(store.state.main.packingSlips).filter(
        (packingSlip) => packingSlip.orderID === this.id
      ),
      ["receivedDate", "created"]
    );
  }

  get issues() {
    return this.data.issues
      .map((id) => store.state.main.issues[id])
      .filter(Boolean);
  }

  get document() {
    return this.data.document ? new DocumentRef(this.data.document) : null;
  }

  get totalCost() {
    return this.data.totalCost !== null
      ? formatCurrency(this.data.totalCost)
      : null;
  }

  get saleLabors() {
    return this.data.saleLabors
      .map((id) => store.state.main.saleLabors[id])
      .filter(Boolean);
  }

  get laborer() {
    return this.data.laborer
      ? store.state.main.contacts[this.data.laborer] || null
      : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  get status() {
    const isLabor = this.isLabor;
    if (isLabor) {
      // Labor Order
      return "scheduling";
    } else if (this.data.noMaterial) {
      // Non-Material Order
      if (!this.data.fullyAcked) {
        return "ack";
      }
    } else {
      // Material Order
      if (!this.data.fullyResolvedCritical) {
        return "issuesCritical";
      } else if (!this.data.fullyAcked) {
        return "ack";
      } else if (this.isAwaitingQC) {
        return "qc";
      } else if (!this.data.fullyReceived) {
        return "production";
      } else if (!this.data.qcInspected) {
        return "qc";
      }
    }
    if (!this.data.fullyResolvedCritical) {
      return "issuesCritical";
    } else if (!this.data.fullyBilled) {
      return "billing";
    } else if (!this.data.fullyResolved) {
      return "issues";
    } else if (!this.data.fullyPaid) {
      return "paying";
    } else {
      return "complete";
    }
  }

  get statusType() {
    const type = ORDER_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}
